import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useState } from "react";
import { AlertTriangle, Download, ExternalLink, PenLine, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { useAuth } from "@/lib/auth";
import { DecisaoDeRetrabalho } from "./DecisaoDeRetrabalho";
import { RequestReworkDialog, type ReworkTarget } from "@/components/prosthesis/RequestReworkDialog";
import { serviceLabel } from "./production-types";
import { STATUS_TERMINAIS, formatBRL, formatDate } from "@/components/prosthesis/prosthesis-meta";
import { ProsthesisTeethDisplay } from "@/components/prosthesis/ProsthesisTeethDisplay";
import { EntityFilesPanel } from "@/components/files/EntityFilesPanel";
import { ColorSelect } from "./ColorSelect";
import { ProductionFinalizeButton, ProductionStatusBadge } from "./ProductionStatusControl";
import { EditarServicoDialog } from "./EditarServicoDialog";
import { MateriaisDoServico } from "./MateriaisDoServico";
import { usePendenciaDeMaterial } from "./use-pendencia-material";
import type { IncomingOrderRow, ServiceRow } from "./production-types";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";
import { EntregaDoPedidoBloco } from "./EntregaDoPedidoBloco";
import { MenuDeImpressao } from "./MenuDeImpressao";
import { codigoDoServico } from "@/lib/codigo-do-pedido";
import { ehDonoOuSocio } from "@bookdental/schemas";
import { valorDoServicoEmTexto } from "@/components/prosthesis/valor-do-pedido";

const NO_STAGE = "__none__";

async function downloadAttachment(utils: ReturnType<typeof trpc.useUtils>, fileId: string) {
  try {
    const { url } = await utils.file.getDownloadUrl.fetch({ fileId });
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    toast.error("Não foi possível baixar o arquivo.");
  }
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">{label}</p>
      <p className="text-sm text-zinc-900 dark:text-foreground">{value}</p>
    </div>
  );
}

/** O que fica no lugar do seletor colorido pra quem só tem `production.view`: mesma cor, sem
 *  afordância de clique. */
function ValorEstatico({ rotulo, cor }: { rotulo: string; cor: string | null }) {
  const base = cor ?? "#71717a";
  return (
    <p
      className="mt-0.5 truncate rounded-md px-1.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: `${base}1a`, color: base }}
    >
      {rotulo}
    </p>
  );
}

function IdField({ label, id, className }: { label: string; id: string; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-400 dark:text-muted-foreground/70">{label}</p>
      <p className="select-all break-all font-mono text-[10px] text-zinc-400 dark:text-muted-foreground/70">{id}</p>
    </div>
  );
}

export function ProductionServiceDetailDialog({
  row,
  onClose,
  onOpenOrder,
}: {
  row: ServiceRow | null;
  onClose: () => void;
  onOpenOrder: (order: IncomingOrderRow) => void;
}) {
  const utils = trpc.useUtils();
  const [editando, setEditando] = useState(false);
  const [refazendo, setRefazendo] = useState<ReworkTarget>(null);
  const podeGerenciar = usePermissao("production.manage");
  const { user } = useAuth();
  const ehDonoDoLab = ehDonoOuSocio(user?.activeOrganization?.role);
  // Muda status/rollup do pedido — invalida os dois lados + contadores do dashboard, não só a
  // lista de produção do laboratório.
  const invalidateAfterStatusChange = () => {
    utils.prosthesis.incoming.list.invalidate();
    utils.prosthesis.incoming.getById.invalidate();
    utils.prosthesis.list.invalidate();
    utils.prosthesis.countByStatus.invalidate();
  };
  const setStatus = trpc.prosthesis.incoming.setStatus.useMutation({
    onSuccess: invalidateAfterStatusChange,
  });
  const stagesQuery = trpc.prosthesis.stages.list.useQuery();
  const setProductionStage = trpc.prosthesis.incoming.setProductionStage.useMutation({
    onSuccess: invalidateAfterStatusChange,
  });
  const teamQuery = trpc.team.list.useQuery();
  const setAssignedTo = trpc.prosthesis.incoming.setAssignedTo.useMutation({
    onSuccess: () => utils.prosthesis.incoming.list.invalidate(),
  });
  const activeMembers = (teamQuery.data ?? []).filter((m: any) => m.status === "ACTIVE");
  const { motivo: pendenciaDeMaterial } = usePendenciaDeMaterial(row?.service.id);
  const modelStagesQuery = trpc.prosthesis.modelStages.list.useQuery();
  const setModelStage = trpc.prosthesis.incoming.setModelStage.useMutation({
    onSuccess: () => utils.prosthesis.incoming.list.invalidate(),
  });

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto dark:bg-card">
        {row && (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pr-8">
                <DialogTitle ajuda={AJUDA_PRODUCAO.detalheDoServico}>{row.order.patientName}</DialogTitle>
                <div className="ml-auto">
                  <MenuDeImpressao order={row.order} servico={row.service} />
                </div>
              </div>
              <DialogDescription>
                {[
                  `Serviço ${codigoDoServico(row.order, row.service)}`,
                  row.order.services.length > 1 ? `${row.service.ordem ?? 1} de ${row.order.services.length} serviços do pedido` : null,
                  row.order.organization.isPersonal ? null : row.order.organization.name,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="detalhes">
              <TabsList className="grid h-auto w-full grid-cols-3 bg-zinc-100 p-1 dark:bg-muted">
                <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
                <TabsTrigger value="materiais" className="gap-1.5">
                  Produção
                  {/* O ponto âmbar é o que sustenta a decisão de avisar em vez de bloquear: a
                      pendência precisa ser visível sem abrir a aba. */}
                  {pendenciaDeMaterial && (
                    <span
                      title="Material pendente — o custo deste trabalho está incompleto"
                      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                    />
                  )}
                </TabsTrigger>
                <TabsTrigger value="arquivos">Arquivos</TabsTrigger>
              </TabsList>

              <TabsContent value="detalhes" className="mt-4 space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-border dark:bg-background/50">
                    <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                      Detalhes do Pedido
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <Field label="Dentista" value={row.order.dentistName ?? "—"} />
                      <Field label="Data do pedido" value={formatDate(row.order.createdAt)} />
                      <Field label="Valor" value={row.service.priceCents == null ? formatBRL(null) : valorDoServicoEmTexto(row.service)} />
                      <Field label="Prazo" value={formatDate(row.order.deadline)} />
                      <IdField label="ID do pedido" id={row.order.id} />
                      <IdField label="ID do serviço" id={row.service.id} />
                    </div>
                  </div>

                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-border dark:bg-background/50">
                    <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                      Produção
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                          Status
                          {row.service.hasOpenRework && (
                            <span title="Retrabalho em aberto" className="text-red-600 dark:text-red-400">
                              <AlertTriangle className="h-3 w-3" />
                            </span>
                          )}
                        </p>
                        <div className="mt-0.5">
                          <ProductionStatusBadge status={row.service.status} />
                        </div>
                      </div>

                      {(stagesQuery.data?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                            Estágio interno
                          </p>
                          {podeGerenciar ? (
                            <ColorSelect
                              size="md"
                              className="mt-0.5 w-full"
                              value={row.service.productionStageId ?? NO_STAGE}
                              disabled={setProductionStage.isPending}
                              onChange={(v) =>
                                setProductionStage.mutate({
                                  serviceId: row.service.id,
                                  productionStageId: v === NO_STAGE ? null : v,
                                })
                              }
                              options={[
                                { value: NO_STAGE, label: "Nenhum", bg: "#f4f4f5", color: "#71717a" },
                                ...stagesQuery.data!.map((s) => ({
                                  value: s.id,
                                  label: s.label,
                                  bg: `${s.colorHex ?? "#2457ff"}1a`,
                                  color: s.colorHex ?? "#2457ff",
                                })),
                              ]}
                            />
                          ) : (
                            <ValorEstatico
                              rotulo={row.service.productionStage?.label ?? "Nenhum"}
                              cor={row.service.productionStage?.colorHex ?? null}
                            />
                          )}
                        </div>
                      )}

                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                          Responsável
                        </p>
                        {podeGerenciar ? (
                          <select
                            value={row.service.assignedToUserId ?? ""}
                            disabled={setAssignedTo.isPending}
                            onChange={(e) =>
                              setAssignedTo.mutate({
                                serviceId: row.service.id,
                                assignedToUserId: e.target.value || null,
                              })
                            }
                            className="mt-0.5 h-7 w-full truncate rounded-md border border-zinc-200 bg-white px-1.5 text-xs font-medium text-zinc-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
                          >
                            <option value="">Ninguém</option>
                            {activeMembers.map((m: any) => (
                              <option key={m.user.id} value={m.user.id}>
                                {m.user.name ?? m.user.email}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="mt-0.5 truncate text-xs font-medium text-zinc-800 dark:text-foreground">
                            {row.service.assignedToUser?.name ?? "Ninguém"}
                          </p>
                        )}
                      </div>

                      {(modelStagesQuery.data?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                            Status do Modelo
                          </p>
                          {podeGerenciar ? (
                            <ColorSelect
                              size="md"
                              className="mt-0.5 w-full"
                              value={row.service.modelStageId ?? NO_STAGE}
                              disabled={setModelStage.isPending}
                              onChange={(v) =>
                                setModelStage.mutate({
                                  serviceId: row.service.id,
                                  modelStageId: v === NO_STAGE ? null : v,
                                })
                              }
                              options={[
                                { value: NO_STAGE, label: "Nenhum", bg: "#f4f4f5", color: "#71717a" },
                                ...modelStagesQuery.data!.map((s) => ({
                                  value: s.id,
                                  label: s.label,
                                  bg: `${s.colorHex ?? "#78716c"}1a`,
                                  color: s.colorHex ?? "#78716c",
                                })),
                              ]}
                            />
                          ) : (
                            <ValorEstatico
                              rotulo={row.service.modelStage?.label ?? "Nenhum"}
                              cor={row.service.modelStage?.colorHex ?? null}
                            />
                          )}
                        </div>
                      )}

                      <Field label="Prazo de Produção" value={formatDate(row.order.deadline)} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-8 sm:flex-row">
                  <div className="w-full shrink-0 sm:w-[150px]">
                    <ProsthesisTeethDisplay teeth={row.service.teeth} />
                  </div>
                  <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-3">
                    <Field label="Serviço" value={row.service.serviceCategory.label} />
                    <Field label="Dentes" value={row.service.teeth || "—"} />
                    <Field
                      label="Material"
                      value={rotuloDoMaterialDoServico(row.service) ?? "—"}
                    />
                    <Field label="Cor" value={row.service.shadeOption?.code ?? "—"} />
                    <Field label="Observações" value={row.service.notes || "—"} className="col-span-2" />
                  </div>
                </div>

                <EntregaDoPedidoBloco order={row.order} />
              </TabsContent>

              <TabsContent value="materiais" className="mt-4">
                <MateriaisDoServico serviceId={row.service.id} />
              </TabsContent>

              <TabsContent value="arquivos" className="mt-4">
                <EntityFilesPanel relatedType="PROSTHESIS_SERVICE" relatedId={row.service.id} />
              </TabsContent>
            </Tabs>

            <ReworkHistory
              serviceId={row.service.id}
              precoDoServicoCents={row.service.priceCents ?? null}
              podeDecidir={ehDonoDoLab}
            />

            <div className="grid grid-cols-3 items-center gap-2 border-t border-zinc-100 pt-3 dark:border-border">
              <div className="flex flex-wrap items-center gap-2 justify-self-start">
                {/* Editar o serviço vive aqui, e não só no diálogo do pedido: depois do aceite o
                    trabalho passa a ser tocado por esta tela, e era justamente aí que faltava a
                    edição. O servidor nunca restringiu por status — a trava era a ausência do
                    botão. */}
                {/* Refazer só faz sentido no que já foi dado por pronto: se ainda está em
                    produção, continuar trabalhando não é retrabalho, é o trabalho. */}
                {podeGerenciar && row.service.status === "CONCLUIDO" && (
                  <button
                    type="button"
                    onClick={() =>
                      setRefazendo({
                        serviceId: row.service.id,
                        serviceLabel: serviceLabel(row.service),
                        patientName: row.order.patientName,
                      })
                    }
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-950/20"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Refazer serviço
                  </button>
                )}
                {/* Editar some no que já terminou. Alterar a especificação de um serviço ENTREGUE
                    reescreveria o passado: o registro passaria a dizer que foi entregue uma coisa
                    diferente da que saiu, mantendo a mesma data de entrega. O caminho honesto é
                    Refazer — reabre (limpa a data), aí edita, aí conclui de novo, e a trilha conta
                    o que aconteceu de verdade. Em cancelado e recusado editar não serve a nada. */}
                {podeGerenciar && !STATUS_TERMINAIS.includes(row.service.status) && (
                <button
                  type="button"
                  onClick={() => setEditando(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-500/10"
                >
                  <PenLine className="h-3.5 w-3.5" />
                  Editar serviço
                </button>
                )}
                {row.service.attachmentFile && (
                  <button
                    type="button"
                    onClick={() => downloadAttachment(utils, row.service.attachmentFile!.id)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-border dark:text-primary dark:hover:bg-primary/10"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Baixar arquivo anexado
                  </button>
                )}
              </div>

              <div className="justify-self-center">
                {podeGerenciar && (
                  <ProductionFinalizeButton
                    status={row.service.status}
                    disabled={setStatus.isPending}
                    variant="button"
                    serviceId={row.service.id}
                    onConclude={() => setStatus.mutate({ serviceId: row.service.id, status: "CONCLUIDO" })}
                    onCancel={() => setStatus.mutate({ serviceId: row.service.id, status: "CANCELADO" })}
                  />
                )}
              </div>

              <button
                type="button"
                onClick={() => onOpenOrder(row.order)}
                className="inline-flex items-center gap-1.5 justify-self-end rounded-md bg-brand-50 px-2.5 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100 dark:bg-primary/10 dark:text-primary dark:hover:bg-primary/20"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Ver pedido completo
              </button>
            </div>
          </>
        )}
      </DialogContent>

      <RequestReworkDialog target={refazendo} lado="laboratorio" onClose={() => setRefazendo(null)} />

      {row && (
        <EditarServicoDialog
          aberto={editando}
          servico={row.service}
          onFechar={() => setEditando(false)}
        />
      )}
    </Dialog>
  );
}

/** Histórico de solicitações de retrabalho do dentista — direto no corpo do diálogo (não é mais
 *  uma aba: se o serviço nunca passou por retrabalho, não mostra nada aqui). Arquivos anexados
 *  ficam na aba Arquivos (mesmo módulo genérico), aqui é só o texto + quem pediu + quando. */
function ReworkHistory({
  serviceId,
  precoDoServicoCents,
  podeDecidir,
}: {
  serviceId: string;
  precoDoServicoCents: number | null;
  /** Decidir cobrança é decisão comercial — só o dono do laboratório (mesma régua do servidor). */
  podeDecidir: boolean;
}) {
  const query = trpc.prosthesis.reworkRequests.useQuery({ serviceId });
  const requests = query.data ?? [];

  if (query.isLoading || requests.length === 0) return null;

  return (
    <div className="mt-4 space-y-2">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
        <RotateCcw className="h-3.5 w-3.5" />
        Retrabalho
      </p>
      <ul className="space-y-2">
        {requests.map((r) => (
          <li key={r.id} className="rounded-lg border border-red-200 bg-red-50/50 p-3 dark:border-red-900/40 dark:bg-red-950/10">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-red-700 dark:text-red-400">
                {r.user?.name ?? (r.origem === "LABORATORIO" ? "Laboratório" : "Dentista")}
                <span className="ml-1.5 font-normal text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {r.origem === "LABORATORIO" ? "· reabriu pra refazer" : "· solicitou retrabalho"}
                </span>
              </p>
              <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{formatDate(r.createdAt)}</p>
            </div>
            <p className="text-sm text-zinc-700 dark:text-foreground/80">{r.notes}</p>
            {podeDecidir && (
              <DecisaoDeRetrabalho
                reworkId={r.id}
                serviceId={serviceId}
                precoDoServicoCents={precoDoServicoCents}
                causa={r.causa}
                cobrar={r.cobrar}
                valorCents={r.valorCents}
                decididoPor={r.decididoPor}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
